/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getI64Decoder,
  getI64Encoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getU64Decoder,
  getU64Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectAddress,
  expectSome,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";

export const CREATE_ESCROW_DISCRIMINATOR = new Uint8Array([
  253, 215, 165, 116, 36, 108, 68, 80,
]);

export function getCreateEscrowDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    CREATE_ESCROW_DISCRIMINATOR,
  );
}

export type CreateEscrowInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountEscrow extends string | AccountMeta<string> = string,
  TAccountAgent extends string | AccountMeta<string> = string,
  TAccountClientTokenAccount extends string | AccountMeta<string> = string,
  TAccountEscrowVault extends string | AccountMeta<string> = string,
  TAccountTokenMint extends string | AccountMeta<string> = string,
  TAccountClient extends string | AccountMeta<string> = string,
  TAccountTokenProgram extends string | AccountMeta<string> =
    "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA",
  TAccountSystemProgram extends string | AccountMeta<string> =
    "11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountEscrow extends string
        ? WritableAccount<TAccountEscrow>
        : TAccountEscrow,
      TAccountAgent extends string
        ? ReadonlyAccount<TAccountAgent>
        : TAccountAgent,
      TAccountClientTokenAccount extends string
        ? WritableAccount<TAccountClientTokenAccount>
        : TAccountClientTokenAccount,
      TAccountEscrowVault extends string
        ? WritableAccount<TAccountEscrowVault>
        : TAccountEscrowVault,
      TAccountTokenMint extends string
        ? ReadonlyAccount<TAccountTokenMint>
        : TAccountTokenMint,
      TAccountClient extends string
        ? WritableSignerAccount<TAccountClient> &
            AccountSignerMeta<TAccountClient>
        : TAccountClient,
      TAccountTokenProgram extends string
        ? ReadonlyAccount<TAccountTokenProgram>
        : TAccountTokenProgram,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type CreateEscrowInstructionData = {
  discriminator: ReadonlyUint8Array;
  escrowId: bigint;
  amount: bigint;
  jobDescription: string;
  deadline: bigint;
};

export type CreateEscrowInstructionDataArgs = {
  escrowId: number | bigint;
  amount: number | bigint;
  jobDescription: string;
  deadline: number | bigint;
};

export function getCreateEscrowInstructionDataEncoder(): Encoder<CreateEscrowInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["escrowId", getU64Encoder()],
      ["amount", getU64Encoder()],
      [
        "jobDescription",
        addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
      ],
      ["deadline", getI64Encoder()],
    ]),
    (value) => ({ ...value, discriminator: CREATE_ESCROW_DISCRIMINATOR }),
  );
}

export function getCreateEscrowInstructionDataDecoder(): Decoder<CreateEscrowInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["escrowId", getU64Decoder()],
    ["amount", getU64Decoder()],
    ["jobDescription", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["deadline", getI64Decoder()],
  ]);
}

export function getCreateEscrowInstructionDataCodec(): Codec<
  CreateEscrowInstructionDataArgs,
  CreateEscrowInstructionData
> {
  return combineCodec(
    getCreateEscrowInstructionDataEncoder(),
    getCreateEscrowInstructionDataDecoder(),
  );
}

export type CreateEscrowAsyncInput<
  TAccountEscrow extends string = string,
  TAccountAgent extends string = string,
  TAccountClientTokenAccount extends string = string,
  TAccountEscrowVault extends string = string,
  TAccountTokenMint extends string = string,
  TAccountClient extends string = string,
  TAccountTokenProgram extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  escrow?: Address<TAccountEscrow>;
  agent: Address<TAccountAgent>;
  clientTokenAccount: Address<TAccountClientTokenAccount>;
  escrowVault: Address<TAccountEscrowVault>;
  tokenMint: Address<TAccountTokenMint>;
  client: TransactionSigner<TAccountClient>;
  tokenProgram?: Address<TAccountTokenProgram>;
  systemProgram?: Address<TAccountSystemProgram>;
  escrowId: CreateEscrowInstructionDataArgs["escrowId"];
  amount: CreateEscrowInstructionDataArgs["amount"];
  jobDescription: CreateEscrowInstructionDataArgs["jobDescription"];
  deadline: CreateEscrowInstructionDataArgs["deadline"];
};

export async function getCreateEscrowInstructionAsync<
  TAccountEscrow extends string,
  TAccountAgent extends string,
  TAccountClientTokenAccount extends string,
  TAccountEscrowVault extends string,
  TAccountTokenMint extends string,
  TAccountClient extends string,
  TAccountTokenProgram extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: CreateEscrowAsyncInput<
    TAccountEscrow,
    TAccountAgent,
    TAccountClientTokenAccount,
    TAccountEscrowVault,
    TAccountTokenMint,
    TAccountClient,
    TAccountTokenProgram,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  CreateEscrowInstruction<
    TProgramAddress,
    TAccountEscrow,
    TAccountAgent,
    TAccountClientTokenAccount,
    TAccountEscrowVault,
    TAccountTokenMint,
    TAccountClient,
    TAccountTokenProgram,
    TAccountSystemProgram
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    escrow: { value: input.escrow ?? null, isWritable: true },
    agent: { value: input.agent ?? null, isWritable: false },
    clientTokenAccount: {
      value: input.clientTokenAccount ?? null,
      isWritable: true,
    },
    escrowVault: { value: input.escrowVault ?? null, isWritable: true },
    tokenMint: { value: input.tokenMint ?? null, isWritable: false },
    client: { value: input.client ?? null, isWritable: true },
    tokenProgram: { value: input.tokenProgram ?? null, isWritable: false },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.escrow.value) {
    accounts.escrow.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            103, 104, 111, 115, 116, 95, 112, 114, 111, 116, 101, 99, 116,
          ]),
        ),
        getAddressEncoder().encode(expectAddress(accounts.client.value)),
        getU64Encoder().encode(expectSome(args.escrowId)),
      ],
    });
  }
  if (!accounts.tokenProgram.value) {
    accounts.tokenProgram.value =
      "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA" as Address<"TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA">;
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.escrow),
      getAccountMeta(accounts.agent),
      getAccountMeta(accounts.clientTokenAccount),
      getAccountMeta(accounts.escrowVault),
      getAccountMeta(accounts.tokenMint),
      getAccountMeta(accounts.client),
      getAccountMeta(accounts.tokenProgram),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getCreateEscrowInstructionDataEncoder().encode(
      args as CreateEscrowInstructionDataArgs,
    ),
    programAddress,
  } as CreateEscrowInstruction<
    TProgramAddress,
    TAccountEscrow,
    TAccountAgent,
    TAccountClientTokenAccount,
    TAccountEscrowVault,
    TAccountTokenMint,
    TAccountClient,
    TAccountTokenProgram,
    TAccountSystemProgram
  >);
}

export type CreateEscrowInput<
  TAccountEscrow extends string = string,
  TAccountAgent extends string = string,
  TAccountClientTokenAccount extends string = string,
  TAccountEscrowVault extends string = string,
  TAccountTokenMint extends string = string,
  TAccountClient extends string = string,
  TAccountTokenProgram extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  escrow: Address<TAccountEscrow>;
  agent: Address<TAccountAgent>;
  clientTokenAccount: Address<TAccountClientTokenAccount>;
  escrowVault: Address<TAccountEscrowVault>;
  tokenMint: Address<TAccountTokenMint>;
  client: TransactionSigner<TAccountClient>;
  tokenProgram?: Address<TAccountTokenProgram>;
  systemProgram?: Address<TAccountSystemProgram>;
  escrowId: CreateEscrowInstructionDataArgs["escrowId"];
  amount: CreateEscrowInstructionDataArgs["amount"];
  jobDescription: CreateEscrowInstructionDataArgs["jobDescription"];
  deadline: CreateEscrowInstructionDataArgs["deadline"];
};

export function getCreateEscrowInstruction<
  TAccountEscrow extends string,
  TAccountAgent extends string,
  TAccountClientTokenAccount extends string,
  TAccountEscrowVault extends string,
  TAccountTokenMint extends string,
  TAccountClient extends string,
  TAccountTokenProgram extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: CreateEscrowInput<
    TAccountEscrow,
    TAccountAgent,
    TAccountClientTokenAccount,
    TAccountEscrowVault,
    TAccountTokenMint,
    TAccountClient,
    TAccountTokenProgram,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): CreateEscrowInstruction<
  TProgramAddress,
  TAccountEscrow,
  TAccountAgent,
  TAccountClientTokenAccount,
  TAccountEscrowVault,
  TAccountTokenMint,
  TAccountClient,
  TAccountTokenProgram,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    escrow: { value: input.escrow ?? null, isWritable: true },
    agent: { value: input.agent ?? null, isWritable: false },
    clientTokenAccount: {
      value: input.clientTokenAccount ?? null,
      isWritable: true,
    },
    escrowVault: { value: input.escrowVault ?? null, isWritable: true },
    tokenMint: { value: input.tokenMint ?? null, isWritable: false },
    client: { value: input.client ?? null, isWritable: true },
    tokenProgram: { value: input.tokenProgram ?? null, isWritable: false },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.tokenProgram.value) {
    accounts.tokenProgram.value =
      "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA" as Address<"TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA">;
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.escrow),
      getAccountMeta(accounts.agent),
      getAccountMeta(accounts.clientTokenAccount),
      getAccountMeta(accounts.escrowVault),
      getAccountMeta(accounts.tokenMint),
      getAccountMeta(accounts.client),
      getAccountMeta(accounts.tokenProgram),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getCreateEscrowInstructionDataEncoder().encode(
      args as CreateEscrowInstructionDataArgs,
    ),
    programAddress,
  } as CreateEscrowInstruction<
    TProgramAddress,
    TAccountEscrow,
    TAccountAgent,
    TAccountClientTokenAccount,
    TAccountEscrowVault,
    TAccountTokenMint,
    TAccountClient,
    TAccountTokenProgram,
    TAccountSystemProgram
  >);
}

export type ParsedCreateEscrowInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    escrow: TAccountMetas[0];
    agent: TAccountMetas[1];
    clientTokenAccount: TAccountMetas[2];
    escrowVault: TAccountMetas[3];
    tokenMint: TAccountMetas[4];
    client: TAccountMetas[5];
    tokenProgram: TAccountMetas[6];
    systemProgram: TAccountMetas[7];
  };
  data: CreateEscrowInstructionData;
};

export function parseCreateEscrowInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedCreateEscrowInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 8) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      escrow: getNextAccount(),
      agent: getNextAccount(),
      clientTokenAccount: getNextAccount(),
      escrowVault: getNextAccount(),
      tokenMint: getNextAccount(),
      client: getNextAccount(),
      tokenProgram: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getCreateEscrowInstructionDataDecoder().decode(instruction.data),
  };
}
